import { adminFetch } from '../../lib/adminFetch';
import React, { useState, useEffect, useRef } from 'react';

const emptyCustomer = {
  id: '',
  name: '',
  email: '',
  type: 'company',
  salesStatus: '見込み',
  srwithContract: 'なし',
  academyStatus: '未受講',
  memo: '',
  fields: [],
};

const salesStatusOptions = ['見込み', '商談中', '休止'];
const srwithContractOptions = ['なし', 'あり'];
const academyStatusOptions = ['未受講', '受講中', '解約済み'];
const typeLabels = { company: '企業', sr: '社労士' };

const CRMAdmin = ({ filter = 'all' }) => {
  // 顧客リストは数万件規模のため、一覧はサーバー側で絞り込み・並び替え・ページングして取得する（admin-crm.php）
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize] = useState(50);
  const [listLoading, setListLoading] = useState(false);
  const [isNewForm, setIsNewForm] = useState(false);
  const [selected, setSelected] = useState(null);
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState(emptyCustomer);
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState('');
  const [importPreview, setImportPreview] = useState(null);
  const [sortKey, setSortKey] = useState('name');
  const [sortAsc, setSortAsc] = useState(true);
  const fileInputRef = useRef(null);

  const [events, setEvents] = useState([]);
  const [mlLists, setMlLists] = useState([]);

  // 検索は入力の都度サーバーに投げないよう300msデバウンス
  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search), 300);
    return () => clearTimeout(t);
  }, [search]);

  // 絞り込み条件が変わったら1ページ目に戻す
  useEffect(() => { setPage(1); }, [filter, debouncedSearch, sortKey, sortAsc]);

  const loadList = () => {
    setListLoading(true);
    const params = new URLSearchParams({
      action: 'list', filter, search: debouncedSearch, sortKey, sortAsc: sortAsc ? '1' : '0',
      page: String(page), pageSize: String(pageSize), t: String(Date.now()),
    });
    adminFetch('/api/admin-crm.php?' + params.toString())
      .then((r) => (r.ok ? r.json() : { items: [], total: 0 }))
      .then((data) => { setItems(data.items || []); setTotal(data.total || 0); })
      .catch(() => { setItems([]); setTotal(0); })
      .finally(() => setListLoading(false));
  };

  useEffect(() => { loadList(); }, [filter, debouncedSearch, sortKey, sortAsc, page]); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchMlLists = () => {
    adminFetch('/api/admin-read.php?file=ml-lists&t=' + Date.now()).then((r) => r.ok ? r.json() : []).then(setMlLists).catch(() => setMlLists([]));
  };

  useEffect(() => {
    adminFetch('/api/admin-read.php?file=events&t=' + Date.now()).then((r) => r.ok ? r.json() : []).then(setEvents).catch(() => setEvents([]));
    fetchMlLists();
  }, []);

  // 顧客が属するMLリスト
  const getCustomerMlLists = (email) => mlLists.filter((l) => (l.emails || []).includes(email));

  // MLリストに顧客を追加/削除
  const toggleMlList = async (listId, email, add) => {
    const updated = mlLists.map((l) => {
      if (l.id !== listId) return l;
      const emails = new Set(l.emails || []);
      if (add) emails.add(email); else emails.delete(email);
      return { ...l, emails: [...emails] };
    });
    try {
      await adminFetch('/api/admin-data.php', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ file: 'ml-lists', data: updated }) });
      setMlLists(updated);
    } catch {}
  };

  const getEventName = (id) => {
    const ev = events.find((e) => e.id === id);
    return ev ? ev.name : id;
  };

  // フォーム用ヘルパー: 姓名・会社名・電話番号をfieldsから取り出し
  const getFormField = (f, key) => (f.fields || []).find((x) => x.key === key)?.value || '';
  const setFormField = (key, value) => {
    setForm((prev) => {
      const fields = (prev.fields || []).filter((x) => x.key !== key);
      if (value) fields.push({ key, value });
      return { ...prev, fields };
    });
  };
  const formSei = form.name ? form.name.split(/\s+/)[0] || '' : '';
  const formMei = form.name ? form.name.split(/\s+/).slice(1).join(' ') || '' : '';
  const setFormName = (sei, mei) => setForm((prev) => ({ ...prev, name: (sei + ' ' + mei).trim() }));

  const startNew = async () => {
    await fetchMlLists();
    setForm({ ...emptyCustomer, id: Date.now() });
    setFormMlListIds([]);
    setIsNewForm(true);
    setEditing(true);
    setSelected(null);
  };

  const startEdit = async () => {
    if (!selected) return;
    const res = await adminFetch('/api/admin-read.php?file=ml-lists&t=' + Date.now());
    const latestLists = res.ok ? await res.json() : mlLists;
    setMlLists(latestLists);
    setForm({ ...selected, fields: [...(selected.fields || [])] });
    setFormMlListIds(latestLists.filter((l) => (l.emails || []).includes(selected.email)).map((l) => l.id));
    setIsNewForm(false);
    setEditing(true);
  };

  const cancelEdit = () => {
    setEditing(false);
    setForm(emptyCustomer);
  };

  // 編集フォーム用: この顧客が所属するMLリスト
  const [formMlListIds, setFormMlListIds] = useState([]);

  const saveCustomer = async () => {
    setSaving(true);
    try {
      const res = await adminFetch('/api/admin-crm.php?action=save', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ customer: form }),
      });
      const result = await res.json();
      if (result.success) {
        setMessage('保存しました');
        setTimeout(() => setMessage(''), 2000);
      } else {
        setMessage(result.message || '保存に失敗しました');
      }
    } catch { setMessage('保存に失敗しました'); }
    setSaving(false);

    // MLリスト更新: フォームで選択されたリストに追加/削除
    if (form.email) {
      const updatedLists = mlLists.map((l) => {
        const emails = new Set(l.emails || []);
        if (formMlListIds.includes(l.id)) {
          emails.add(form.email);
        } else {
          emails.delete(form.email);
        }
        return { ...l, emails: [...emails] };
      });
      try {
        await adminFetch('/api/admin-data.php', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ file: 'ml-lists', data: updatedLists }) });
        setMlLists(updatedLists);
      } catch {}
    }

    setEditing(false);
    setSelected(form);
    setForm(emptyCustomer);
    setFormMlListIds([]);
    loadList();
  };

  const deleteCustomer = async () => {
    if (!selected || !window.confirm(`${selected.name} を削除しますか？`)) return;
    setSaving(true);
    try {
      const res = await adminFetch('/api/admin-crm.php?action=delete', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: selected.id }),
      });
      const result = await res.json();
      if (result.success) { setSelected(null); loadList(); }
      else setMessage(result.message || '削除に失敗しました');
    } catch { setMessage('削除に失敗しました'); }
    setSaving(false);
  };

  const addField = () => {
    setForm((prev) => ({
      ...prev,
      fields: [...prev.fields, { key: '', value: '' }],
    }));
  };

  const updateField = (index, prop, value) => {
    setForm((prev) => ({
      ...prev,
      fields: prev.fields.map((f, i) => i === index ? { ...f, [prop]: value } : f),
    }));
  };

  const removeField = (index) => {
    setForm((prev) => ({
      ...prev,
      fields: prev.fields.filter((_, i) => i !== index),
    }));
  };

  // Excelインポート（PHP API経由）
  const handleExcelFile = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const formData = new FormData();
    formData.append('file', file);
    formData.append('action', 'preview');
    try {
      const res = await adminFetch('/api/admin-import.php', { method: 'POST', body: formData });
      const data = await res.json();
      if (data.success) {
        setImportPreview({ data: data.parsed, totalCount: data.totalCount, skippedList: data.skipped, fileName: file.name, file });
      } else {
        setMessage(data.message || 'インポートに失敗しました');
      }
    } catch { setMessage('インポートに失敗しました'); }
    e.target.value = '';
  };

  const executeImport = async () => {
    if (!importPreview) return;
    const formData = new FormData();
    formData.append('file', importPreview.file);
    formData.append('action', 'import');
    try {
      const res = await adminFetch('/api/admin-import.php', { method: 'POST', body: formData });
      const data = await res.json();
      if (data.success) {
        setMessage(`${data.importedCount}件をインポートしました${data.skippedCount > 0 ? `（重複スキップ: ${data.skippedCount}件）` : ''}`);
        loadList();
      } else {
        setMessage(data.message || 'インポートに失敗しました');
      }
    } catch { setMessage('インポートに失敗しました'); }
    setImportPreview(null);
  };

  // 絞り込み・並び替え・ページングはサーバー側（admin-crm.php）で実施済み。items をそのまま表示する。
  const getField = (c, key) => (c.fields || []).find((f) => f.key === key)?.value || '';

  const toggleSort = (key) => {
    if (sortKey === key) setSortAsc(!sortAsc);
    else { setSortKey(key); setSortAsc(true); }
  };

  const sortIcon = (key) => sortKey === key ? (sortAsc ? ' ▲' : ' ▼') : '';

  const salesStatusColors = {
    '見込み': 'bg-yellow-100 text-yellow-700',
    '商談中': 'bg-blue-100 text-blue-700',
    '休止': 'bg-gray-100 text-gray-500',
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-2xl font-bold">顧客管理</h2>
        <div className="flex gap-2">
          <label className="bg-green-600 text-white px-4 py-2 rounded-lg text-sm font-bold hover:bg-green-700 cursor-pointer">
            Excelインポート
            <input ref={fileInputRef} type="file" accept=".xlsx,.xls,.csv" className="hidden" onChange={handleExcelFile} />
          </label>
          <button onClick={startNew} className="bg-blue-600 text-white px-4 py-2 rounded-lg text-sm font-bold hover:bg-blue-700">
            + 新規登録
          </button>
        </div>
      </div>

      {message && <div className="mb-4 px-4 py-2 bg-green-100 text-green-800 rounded-lg text-sm">{message}</div>}

      {/* インポートプレビュー */}
      {importPreview && (
        <div className="mb-4 bg-white rounded-xl shadow p-6">
          <h3 className="font-bold text-lg mb-3">インポートプレビュー: {importPreview.fileName}</h3>
          <div className="flex gap-4 mb-3 text-sm">
            <span className="font-bold text-blue-700">取り込み: {(importPreview.totalCount || importPreview.data.length)}件</span>
            {importPreview.skippedList.length > 0 && <span className="font-bold text-orange-600">重複スキップ: {importPreview.skippedList.length}件</span>}
          </div>
          <div className="max-h-60 overflow-y-auto border rounded-lg mb-4">
            <table className="w-full text-xs">
              <thead className="bg-gray-50 sticky top-0">
                <tr>
                  <th className="text-left px-3 py-2">名前</th>
                  <th className="text-left px-3 py-2">メール</th>
                  <th className="text-left px-3 py-2">区分</th>
                  <th className="text-left px-3 py-2">会社名</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {importPreview.data.slice(0, 50).map((c, i) => (
                  <tr key={i} className="hover:bg-gray-50">
                    <td className="px-3 py-1.5">{c.name}</td>
                    <td className="px-3 py-1.5">{c.email}</td>
                    <td className="px-3 py-1.5">{c.type === 'sr' ? '社労士' : '企業'}</td>
                    <td className="px-3 py-1.5">{c.fields.find((f) => f.key === '会社名')?.value || ''}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            {(importPreview.totalCount || importPreview.data.length) > 50 && <p className="text-center text-xs text-gray-400 py-2">他 {(importPreview.totalCount || importPreview.data.length) - 50}件...</p>}
          </div>
          {/* スキップ一覧 */}
          {importPreview.skippedList.length > 0 && (
            <details className="mb-4">
              <summary className="text-sm font-bold text-orange-600 cursor-pointer hover:underline">
                スキップされた {importPreview.skippedList.length}件を表示
              </summary>
              <div className="mt-2 max-h-40 overflow-y-auto border border-orange-200 rounded-lg">
                <table className="w-full text-xs">
                  <thead className="bg-orange-50 sticky top-0">
                    <tr>
                      <th className="text-left px-3 py-2">名前</th>
                      <th className="text-left px-3 py-2">メール</th>
                      <th className="text-left px-3 py-2">理由</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y">
                    {importPreview.skippedList.map((s, i) => (
                      <tr key={i}>
                        <td className="px-3 py-1.5">{s.name}</td>
                        <td className="px-3 py-1.5">{s.email}</td>
                        <td className="px-3 py-1.5 text-orange-600">{s.reason}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </details>
          )}
          <div className="flex gap-3">
            <button onClick={executeImport} disabled={saving} className="bg-blue-600 text-white px-6 py-2 rounded-lg text-sm font-bold hover:bg-blue-700 disabled:opacity-50">
              {saving ? 'インポート中...' : `${(importPreview.totalCount || importPreview.data.length)}件をインポート`}
            </button>
            <button onClick={() => setImportPreview(null)} className="bg-gray-200 text-gray-700 px-6 py-2 rounded-lg text-sm font-bold hover:bg-gray-300">
              キャンセル
            </button>
          </div>
        </div>
      )}

      {/* 検索 */}
      <div className="mb-4">
        <input
          type="text"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="名前・メールアドレスで検索"
          className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
        />
      </div>

      <div className="flex gap-6">
        {/* 顧客一覧 */}
        <div className="w-1/2">
          <div className="mb-1 text-right text-xs text-gray-400 font-bold">{listLoading ? '読み込み中...' : `全${total}件`}</div>
          <div className="bg-white rounded-xl shadow overflow-hidden">
            {items.length === 0 ? (
              <div className="p-8 text-center text-gray-500">{listLoading ? '読み込み中...' : '顧客がいません'}</div>
            ) : (
              <>
                <div className="max-h-[70vh] overflow-y-auto">
                  <table className="w-full table-fixed">
                    <colgroup>
                      <col style={{width:'35%'}} />
                      <col style={{width:'30%'}} />
                      <col style={{width:'15%'}} />
                      <col style={{width:'20%'}} />
                    </colgroup>
                    <thead className="bg-gray-50 border-b sticky top-0">
                      <tr>
                        {[
                          { key: 'name', label: '名前' },
                          { key: 'company', label: '会社名' },
                          { key: 'type', label: '区分' },
                          { key: 'status', label: 'ステータス' },
                            ].map((s) => (
                          <th key={s.key} onClick={() => toggleSort(s.key)}
                            className={`text-left px-2 py-2 text-[11px] font-bold cursor-pointer select-none hover:bg-gray-100 ${sortKey === s.key ? 'text-blue-700' : 'text-gray-500'}`}>
                            {s.label}{sortIcon(s.key)}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody className="divide-y">
                      {items.map((customer) => (
                        <tr key={customer.id} onClick={() => { setSelected(customer); setEditing(false); }}
                          className={`cursor-pointer hover:bg-gray-50 transition-colors ${selected?.id === customer.id ? 'bg-blue-50' : ''}`}>
                          <td className="px-2 py-2">
                            <p className="font-bold text-xs truncate">{customer.name}</p>
                            <p className="text-[10px] text-gray-400 truncate">{customer.email}</p>
                          </td>
                          <td className="px-2 py-2 text-xs text-gray-600 truncate">{getField(customer, '会社名')}</td>
                          <td className="px-2 py-1.5">
                            <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded ${customer.type === 'sr' ? 'bg-purple-100 text-purple-700' : 'bg-blue-100 text-blue-700'}`}>
                              {typeLabels[customer.type] || customer.type}
                            </span>
                          </td>
                          <td className="px-2 py-1.5">
                            <div className="flex flex-wrap gap-0.5">
                              <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded ${salesStatusColors[customer.salesStatus] || 'bg-gray-100 text-gray-600'}`}>{customer.salesStatus}</span>
                              {customer.srwithContract === 'あり' && <span className="text-[10px] font-bold px-1.5 py-0.5 rounded bg-green-100 text-green-700">SrWith</span>}
                              {customer.academyStatus === '受講中' && <span className="text-[10px] font-bold px-1.5 py-0.5 rounded bg-emerald-100 text-emerald-700">アカデミー</span>}
                              {customer.academyStatus === '解約済み' && <span className="text-[10px] font-bold px-1.5 py-0.5 rounded bg-red-100 text-red-600">アカデミー解約</span>}
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                {/* ページング */}
                <div className="flex items-center justify-between px-3 py-2 border-t bg-gray-50 text-xs">
                  <span className="text-gray-500">
                    {total === 0 ? '0件' : `${(page - 1) * pageSize + 1}〜${Math.min(page * pageSize, total)}件 / 全${total}件`}
                  </span>
                  <div className="flex items-center gap-2">
                    <button onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={page <= 1}
                      className="px-2.5 py-1 rounded border border-gray-200 disabled:opacity-40 hover:bg-gray-100 font-bold">前へ</button>
                    <span className="text-gray-500">{page} / {Math.max(1, Math.ceil(total / pageSize))}</span>
                    <button onClick={() => setPage((p) => (p * pageSize < total ? p + 1 : p))} disabled={page * pageSize >= total}
                      className="px-2.5 py-1 rounded border border-gray-200 disabled:opacity-40 hover:bg-gray-100 font-bold">次へ</button>
                  </div>
                </div>
              </>
            )}
          </div>
        </div>

        {/* 詳細・編集 */}
        <div className="w-1/2">
          {editing ? (
            <div className="bg-white rounded-xl shadow p-6 space-y-4">
              <h3 className="font-bold text-lg">{isNewForm ? '新規登録' : '編集'}</h3>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-bold mb-1">姓 *</label>
                  <input type="text" value={formSei} onChange={(e) => setFormName(e.target.value, formMei)}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
                </div>
                <div>
                  <label className="block text-sm font-bold mb-1">名</label>
                  <input type="text" value={formMei} onChange={(e) => setFormName(formSei, e.target.value)}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
                </div>
              </div>

              <div>
                <label className="block text-sm font-bold mb-1">会社名</label>
                <input type="text" value={getFormField(form, '会社名')} onChange={(e) => setFormField('会社名', e.target.value)}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-bold mb-1">メールアドレス</label>
                  <input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
                </div>
                <div>
                  <label className="block text-sm font-bold mb-1">電話番号</label>
                  <input type="text" value={getFormField(form, '電話番号')} onChange={(e) => setFormField('電話番号', e.target.value)}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
                </div>
              </div>

              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div>
                  <label className="block text-sm font-bold mb-1">区分</label>
                  <select value={form.type} onChange={(e) => setForm({ ...form, type: e.target.value })}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500">
                    <option value="company">企業</option>
                    <option value="sr">社労士</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-bold mb-1">営業ステータス</label>
                  <select value={form.salesStatus || '見込み'} onChange={(e) => setForm({ ...form, salesStatus: e.target.value })}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500">
                    {salesStatusOptions.map((s) => <option key={s} value={s}>{s}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-bold mb-1">SrWith契約</label>
                  <select value={form.srwithContract || 'なし'} onChange={(e) => setForm({ ...form, srwithContract: e.target.value })}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500">
                    {srwithContractOptions.map((s) => <option key={s} value={s}>{s}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-bold mb-1">アカデミー</label>
                  <select value={form.academyStatus || '未受講'} onChange={(e) => setForm({ ...form, academyStatus: e.target.value })}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500">
                    {academyStatusOptions.map((s) => <option key={s} value={s}>{s}</option>)}
                  </select>
                </div>
              </div>

              {/* MLリスト所属 */}
              {mlLists.length > 0 && (
                <div>
                  <label className="block text-sm font-bold mb-1">MLリスト</label>
                  <div className="flex flex-wrap gap-1.5">
                    {mlLists.map((l) => (
                      <button key={l.id} type="button"
                        onClick={() => setFormMlListIds((prev) => prev.includes(l.id) ? prev.filter((id) => id !== l.id) : [...prev, l.id])}
                        className={`px-3 py-1 rounded-lg text-xs font-bold ${formMlListIds.includes(l.id) ? 'bg-blue-500 text-white' : 'bg-gray-100 text-gray-500 hover:bg-gray-200'}`}>
                        {l.name}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* 自由フィールド */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <label className="text-sm font-bold">追加項目</label>
                  <button onClick={addField} className="text-xs text-blue-600 font-bold hover:underline">+ 項目追加</button>
                </div>
                {form.fields.filter((f) => !['会社名', '電話番号'].includes(f.key)).map((field, i) => {
                  const realIndex = form.fields.indexOf(field);
                  return (
                  <div key={realIndex} className="flex gap-2 mb-2">
                    <input
                      type="text"
                      value={field.key}
                      onChange={(e) => updateField(realIndex, 'key', e.target.value)}
                      placeholder="項目名"
                      className="w-1/3 border border-gray-300 rounded px-2 py-1.5 text-xs focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                    <input
                      type="text"
                      value={field.value}
                      onChange={(e) => updateField(realIndex, 'value', e.target.value)}
                      placeholder="値"
                      className="flex-1 border border-gray-300 rounded px-2 py-1.5 text-xs focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                    <button onClick={() => removeField(realIndex)} className="text-red-500 text-xs font-bold hover:text-red-700">削除</button>
                  </div>
                  );
                })}
              </div>

              <div>
                <label className="block text-sm font-bold mb-1">メモ</label>
                <textarea value={form.memo} onChange={(e) => setForm({ ...form, memo: e.target.value })} rows={3}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none" />
              </div>

              <div className="flex gap-3">
                <button onClick={saveCustomer} disabled={saving || !form.name}
                  className="bg-blue-600 text-white px-6 py-2 rounded-lg text-sm font-bold hover:bg-blue-700 disabled:opacity-50">
                  {saving ? '保存中...' : '保存'}
                </button>
                <button onClick={cancelEdit} className="bg-gray-200 text-gray-700 px-6 py-2 rounded-lg text-sm font-bold hover:bg-gray-300">
                  キャンセル
                </button>
              </div>
            </div>
          ) : selected ? (
            <div className="bg-white rounded-xl shadow p-6">
              <div className="flex items-start justify-between mb-4">
                <div>
                  <h3 className="font-bold text-lg">{selected.name}</h3>
                  <p className="text-sm text-gray-500">{selected.email}</p>
                  <div className="flex gap-1.5 mt-2">
                    <span className={`text-xs font-bold px-2 py-0.5 rounded ${selected.type === 'sr' ? 'bg-purple-100 text-purple-700' : 'bg-blue-100 text-blue-700'}`}>
                      {typeLabels[selected.type] || selected.type}
                    </span>
                    <span className={`text-xs font-bold px-2 py-0.5 rounded ${salesStatusColors[selected.salesStatus] || 'bg-gray-100 text-gray-600'}`}>{selected.salesStatus}</span>
                    {selected.srwithContract === 'あり' && <span className="text-xs font-bold px-2 py-0.5 rounded bg-green-100 text-green-700">SrWith契約</span>}
                    {selected.academyStatus === '受講中' && <span className="text-xs font-bold px-2 py-0.5 rounded bg-emerald-100 text-emerald-700">アカデミー受講中</span>}
                    {selected.academyStatus === '解約済み' && <span className="text-xs font-bold px-2 py-0.5 rounded bg-red-100 text-red-600">アカデミー解約</span>}
                  </div>
                </div>
                <div className="flex gap-2">
                  <button onClick={startEdit} className="bg-gray-100 text-gray-700 px-3 py-1.5 rounded-lg text-xs font-bold hover:bg-gray-200">編集</button>
                  <button onClick={deleteCustomer} className="bg-red-50 text-red-600 px-3 py-1.5 rounded-lg text-xs font-bold hover:bg-red-100">削除</button>
                </div>
              </div>

              {/* 追加項目 */}
              {selected.fields && selected.fields.length > 0 && (
                <div className="border-t pt-4 mb-4">
                  <div className="space-y-2">
                    {selected.fields.map((field, i) => (
                      <div key={i} className="flex gap-2 text-sm">
                        <span className="font-bold text-gray-600 shrink-0">{field.key === 'イベントID' ? 'イベント' : field.key}:</span>
                        <span className="text-gray-700">{field.key === 'イベントID' ? getEventName(field.value) : field.value}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* メモ */}
              {selected.memo && (
                <div className="border-t pt-4">
                  <p className="text-sm font-bold text-gray-600 mb-1">メモ</p>
                  <p className="text-sm text-gray-700 whitespace-pre-wrap bg-gray-50 rounded-lg p-3">{selected.memo}</p>
                </div>
              )}

              {/* MLリスト所属 */}
              {selected.email && (
                <div className="border-t pt-4">
                  <p className="text-sm font-bold text-gray-600 mb-2">MLリスト</p>
                  <div className="flex flex-wrap gap-1.5">
                    {mlLists.map((l) => {
                      const belongs = (l.emails || []).includes(selected.email);
                      return (
                        <button key={l.id} onClick={() => toggleMlList(l.id, selected.email, !belongs)}
                          className={`px-3 py-1 rounded-lg text-xs font-bold ${belongs ? 'bg-blue-500 text-white' : 'bg-gray-100 text-gray-500 hover:bg-gray-200'}`}>
                          {l.name}
                        </button>
                      );
                    })}
                    {mlLists.length === 0 && <span className="text-xs text-gray-400">MLリストがありません</span>}
                  </div>
                </div>
              )}
            </div>
          ) : (
            <div className="bg-white rounded-xl shadow p-8 text-center text-gray-500">
              左の一覧から顧客を選択してください
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default CRMAdmin;
